import {
  addYears,
  formatISO,
  isBefore,
  parseISO,
  startOfToday,
  startOfYear,
} from 'date-fns';

import { getDisplayName, getOrdinalSuffix } from './profile';

export interface CalendarEvent {
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  type: string;
  classNames: string[];
}

export interface Employee {
  id?: string;
  first_name: string;
  last_name: string;
  first_hire_date?: string;
}

export const calculateUpcomingAnniversaries = (
  employees: Employee[],
  showAllYears: boolean = false
): CalendarEvent[] => {
  const today = startOfToday();
  const startOfThisYear = startOfYear(today);
  const oneYearFromToday = addYears(today, 1);

  return employees
    .filter((employee) => employee.first_hire_date)
    .flatMap((employee) => {
      const hireDate = parseISO(employee.first_hire_date!);
      const events: CalendarEvent[] = [];

      // When showAllYears is true (calendar view), show anniversaries from start of current year
      // Otherwise (card view), show only from today forward
      const startDate = showAllYears ? startOfThisYear : today;

      // Calculate this year's anniversary
      const thisYearCount = today.getFullYear() - hireDate.getFullYear();
      const thisYearAnniversary = addYears(hireDate, thisYearCount);

      // Include this year's anniversary if it's after our start date and is a valid anniversary
      if (thisYearCount > 0 && !isBefore(thisYearAnniversary, startDate)) {
        const shouldInclude =
          showAllYears || thisYearAnniversary <= oneYearFromToday;

        if (shouldInclude) {
          events.push({
            title: `${getDisplayName(
              employee
            )}'s ${thisYearCount}${getOrdinalSuffix(
              thisYearCount
            )} Anniversary`,
            start: formatISO(thisYearAnniversary),
            end: formatISO(thisYearAnniversary),
            allDay: true,
            type: 'anniversary',
            classNames: ['anniversary-event'],
          });
        }
      }

      // Calculate next year's anniversary
      const nextYearCount = thisYearCount + 1;
      const nextYearAnniversary = addYears(hireDate, nextYearCount);

      // Include next year's anniversary if appropriate and is a valid anniversary
      const shouldIncludeNextYear =
        (showAllYears || nextYearAnniversary <= oneYearFromToday) &&
        nextYearCount > 0;

      if (shouldIncludeNextYear) {
        events.push({
          title: `${getDisplayName(
            employee
          )}'s ${nextYearCount}${getOrdinalSuffix(nextYearCount)} Anniversary`,
          start: formatISO(nextYearAnniversary),
          end: formatISO(nextYearAnniversary),
          allDay: true,
          type: 'anniversary',
          classNames: ['anniversary-event'],
        });
      }

      return events;
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
};
